import { useEffect, useState } from "react";
import { Link2, Search, Unlink } from "lucide-react";
import { desvincularRealizadoNf, getRealizadoNfCandidatas, vincularRealizadoNf } from "../../api";
import { AvisoErro, Badge, Modal, botaoPequeno, estiloTabela, formatCompetencia, formatData, formatValor, inputStyle } from "./comum";

// Ajuste manual do vínculo NF × solicitação (nota reemitida, excluída ou casada errado no OMIE).
// Vínculo manual não é refeito pela sincronização — ver backend/services/realizado_faturamento_vinculo.py.

export function ModalVinculoNf({ C, nfId, onClose, onAtualizado }) {
  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const T = estiloTabela(C);

  const carregar = async (termo = "") => {
    setBuscando(true);
    setErro("");
    try {
      setDados(await getRealizadoNfCandidatas(nfId, termo));
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscando(false);
    }
  };

  useEffect(() => { carregar(); }, [nfId]); // eslint-disable-line react-hooks/exhaustive-deps

  const executar = async (acao, mensagem) => {
    setSalvando(true);
    setErro("");
    try {
      const r = await acao();
      setAviso(mensagem(r));
      onAtualizado?.();
      await carregar(busca);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const vincular = (s) => {
    const outras = s.nfs.filter((n) => n.status !== "CANCELADO");
    if (outras.length && !window.confirm(
      `A solicitação ${s.id} já tem a(s) NF(s) ${outras.map((n) => n.numero).join(", ")}. Se a nota nova substitui alguma delas, desvincule a antiga primeiro (abra a antiga em Notas fiscais). Vincular mesmo assim, como nota adicional?`,
    )) return;
    executar(() => vincularRealizadoNf(nfId, s.id), (r) =>
      `NF vinculada à solicitação ${r.solicitacaoId}` + (r.competencias.length ? ` (competência ${r.competencias.map(formatCompetencia).join(", ")}).` : " — nenhuma competência livre: entrou como nota adicional."));
  };

  const desvincular = () => {
    if (!window.confirm("Desvincular esta nota? A solicitação volta para aguardando nota e a sincronização não vai casar esta nota de novo sozinha.")) return;
    executar(() => desvincularRealizadoNf(nfId), (r) => `NF desvinculada da solicitação ${r.solicitacaoAnterior}.`);
  };

  const nf = dados?.nf;
  return (
    <Modal C={C} largura={920} titulo={nf ? `Vínculo da NF ${nf.numero} · ${nf.empresa || ""}` : "Vínculo da NF"} onClose={onClose}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {aviso && <div style={{ background: `${C.success}18`, border: `1px solid ${C.success}`, color: C.success, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 10 }}>{aviso}</div>}
      {!nf ? <div style={{ fontSize: 12, color: C.muted }}>Carregando…</div> : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontSize: 12, marginBottom: 12 }}>
            <span>R$ <b>{formatValor(nf.valor)}</b> · emitida em {formatData(nf.dataEmissao)} · CNPJ {nf.cnpj || "—"}</span>
            {nf.status === "CANCELADO" && <Badge cor={C.danger}>Cancelada</Badge>}
            <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              {nf.solicitacaoId ? <>Vinculada à solicitação <b>{nf.solicitacaoId}</b></> : <span style={{ color: C.warning }}>Sem solicitação</span>}
              {nf.solicitacaoId && (
                <button style={botaoPequeno(C.danger, true)} disabled={salvando} onClick={desvincular}><Unlink size={13} /> Desvincular</button>
              )}
            </span>
          </div>
          {nf.vinculoManualPor && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
              Ajustado à mão por {nf.vinculoManualPor} em {formatData(nf.vinculoManualEm)} — a sincronização não altera este vínculo.
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); carregar(busca); }} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por projeto, GP, cliente, descrição ou nº da solicitação" style={{ ...inputStyle(C), flex: 1 }} />
            <button type="submit" style={botaoPequeno(C.brand, true)} disabled={buscando}><Search size={13} /> {buscando ? "Buscando…" : "Buscar"}</button>
          </form>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
            {busca.trim() ? "Resultado da busca, em qualquer data." : "Solicitações da mesma empresa feitas perto da emissão — as mais prováveis primeiro."}
          </div>

          {nf.status === "CANCELADO" ? (
            <div style={{ fontSize: 12, color: C.muted }}>Nota cancelada não pode ser vinculada.</div>
          ) : dados.candidatas.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted }}>Nenhuma solicitação encontrada.</div>
          ) : (
            <div style={{ ...T.wrap, maxHeight: "50vh" }}>
              <table style={{ ...T.table, minWidth: 820 }}>
                <thead><tr>
                  <th style={T.th}>Solicitação</th>
                  <th style={T.th}>Projeto · GP</th>
                  <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                  <th style={T.th}>Competências</th>
                  <th style={T.th}>NFs</th>
                  <th style={T.th} />
                </tr></thead>
                <tbody>
                  {dados.candidatas.map((s) => (
                    <tr key={s.id} style={s.atual ? { background: `${C.brand}10` } : undefined}>
                      <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                        <b>{s.id}</b> · {formatData(s.dataSolicitacao)}
                        <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                          {s.mesmoCnpj && <Badge cor={C.success}>mesmo CNPJ</Badge>}
                          {s.mesmoValor && <Badge cor={C.success}>mesmo valor</Badge>}
                        </div>
                      </td>
                      <td style={{ ...T.td, maxWidth: 240 }}>{s.projeto || "(sem projeto)"}<div style={{ color: C.muted, fontSize: 11 }}>{s.gp || "—"}{s.cliente ? ` · ${s.cliente}` : ""}</div></td>
                      <td style={{ ...T.td, ...T.tdNum }}>{formatValor(s.valor)}</td>
                      <td style={{ ...T.td, fontSize: 11 }}>
                        {s.competencias.map((c) => (
                          <div key={c.competencia}>{formatCompetencia(c.competencia)} · {formatValor(c.valor)}{c.numeroNf ? <span style={{ color: C.muted }}> · NF {c.numeroNf}</span> : ""}</div>
                        ))}
                      </td>
                      <td style={{ ...T.td, fontSize: 11 }}>
                        {s.nfs.length ? s.nfs.map((n) => (
                          <div key={n.numero} style={{ color: n.status === "CANCELADO" ? C.muted : C.text, textDecoration: n.status === "CANCELADO" ? "line-through" : "none" }}>{n.numero}</div>
                        )) : "—"}
                      </td>
                      <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                        {s.atual ? <Badge cor={C.brand}>Atual</Badge> : (
                          <button style={botaoPequeno(C.brand)} disabled={salvando} onClick={() => vincular(s)}><Link2 size={13} /> Vincular</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
